/**
 * @module StorageService
 *
 * Typed, centralized interface for the application's localStorage keys.
 *
 * All localStorage access in the app should go through this service so that:
 * - Key names are defined in one place (no scattered string literals)
 * - Types are enforced at the call site
 * - Tests can swap in a TestStorageService without patching globals
 *
 * @example
 * // Read
 * const user = storage.getUser();
 *
 * // Write
 * storage.setProfile(profile);
 *
 * // Clear (logout)
 * storage.clearSession();
 *
 * // In tests
 * import { TestStorageService } from '../testing/TestStorageService';
 * const storage = new TestStorageService();
 */
import type {
  Factors,
  ImpersonationRecord,
  LocalStorageUser,
  Profile,
} from '../shared/types/appTypes';

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

export const STORAGE_KEYS = {
  USER: 'user',
  PROFILE: 'profile',
  IMPERSONATION: 'impersonation',
  FACTORS: 'factors',
  FILTERS: 'filters',
  TEAMS_API: 'teamsAPI',
  TRAINING_VIDEO_ACK: 'augintel-training-video-ack',
} as const;

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface IStorageService {
  // ---- Reads ---------------------------------------------------------------
  getUser(): LocalStorageUser | null;
  getProfile(): Profile | null;
  getImpersonation(): ImpersonationRecord | null;
  getFactors(): Factors | null;
  getFilters(): string | null;
  getRaw(key: string): string | null;

  // ---- Writes --------------------------------------------------------------
  setUser(user: LocalStorageUser): void;
  setProfile(profile: Profile): void;
  setImpersonation(record: ImpersonationRecord): void;
  setFactors(factors: Factors): void;
  setFilters(filters: unknown): void;
  setRaw(key: string, value: string): void;

  // ---- Removes -------------------------------------------------------------
  removeImpersonation(): void;
  removeTeamsAPI(): void;
  removeFilters(): void;

  // ---- Bulk ----------------------------------------------------------------
  /**
   * Clears the full session (logout). Preserves the training-video-ack key
   * so users don't see the onboarding video again after re-login.
   */
  clearSession(): void;
}

// ---------------------------------------------------------------------------
// Live implementation (uses real localStorage)
// ---------------------------------------------------------------------------

function parseJSON<T>(raw: string | null): T | null {
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export class StorageService implements IStorageService {
  getUser(): LocalStorageUser | null {
    return parseJSON<LocalStorageUser>(localStorage.getItem(STORAGE_KEYS.USER));
  }

  getProfile(): Profile | null {
    return parseJSON<Profile>(localStorage.getItem(STORAGE_KEYS.PROFILE));
  }

  getImpersonation(): ImpersonationRecord | null {
    return parseJSON<ImpersonationRecord>(
      localStorage.getItem(STORAGE_KEYS.IMPERSONATION),
    );
  }

  getFactors(): Factors | null {
    return parseJSON<Factors>(localStorage.getItem(STORAGE_KEYS.FACTORS));
  }

  getFilters(): string | null {
    return localStorage.getItem(STORAGE_KEYS.FILTERS);
  }

  getRaw(key: string): string | null {
    return localStorage.getItem(key);
  }

  setUser(user: LocalStorageUser): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  setProfile(profile: Profile): void {
    localStorage.setItem(STORAGE_KEYS.PROFILE, JSON.stringify(profile));
  }

  setImpersonation(record: ImpersonationRecord): void {
    localStorage.setItem(STORAGE_KEYS.IMPERSONATION, JSON.stringify(record));
  }

  setFactors(factors: Factors): void {
    localStorage.setItem(STORAGE_KEYS.FACTORS, JSON.stringify(factors));
  }

  setFilters(filters: unknown): void {
    localStorage.setItem(STORAGE_KEYS.FILTERS, JSON.stringify(filters));
  }

  setRaw(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  removeImpersonation(): void {
    localStorage.removeItem(STORAGE_KEYS.IMPERSONATION);
  }

  removeTeamsAPI(): void {
    localStorage.removeItem(STORAGE_KEYS.TEAMS_API);
  }

  removeFilters(): void {
    localStorage.removeItem(STORAGE_KEYS.FILTERS);
  }

  clearSession(): void {
    const trainingVideoAck = localStorage.getItem(
      STORAGE_KEYS.TRAINING_VIDEO_ACK,
    );
    localStorage.clear();
    if (trainingVideoAck !== null) {
      localStorage.setItem(STORAGE_KEYS.TRAINING_VIDEO_ACK, trainingVideoAck);
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton — import this in application code
// ---------------------------------------------------------------------------

export const storage = new StorageService();
